import { useFragment } from "@/generated";
import { LessonFieldsFragmentDoc, RestoreLessonsDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { Reference } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for restoring lessons from trash.
 *
 * Provides a mutation function to restore one or more trashed lessons back to active state.
 * Automatically refetches relevant queries to update the UI.
 *
 * @returns An object containing:
 *   - restoreLessons: Function to restore one or more lessons (accepts single ID or array of IDs)
 *   - loading: Boolean indicating if the mutation is in progress
 *   - error: ApolloError if the mutation failed, undefined otherwise
 *
 * @example
 * const { restoreLessons, loading, error } = useRestoreLesson();
 * // Restore single lesson
 * await restoreLessons("lesson123");
 * // Restore multiple lessons
 * await restoreLessons(["lesson1", "lesson2", "lesson3"]);
 */
export const useRestoreLessons = () => {
  const [restoreLessonsMutation, { loading, error }] = useMutation(RestoreLessonsDocument, {
    update(cache, { data }) {
      if (!data?.restoreLessons) return;

      try {
        const lessons = data.restoreLessons.map((lesson) =>
          useFragment(LessonFieldsFragmentDoc, lesson)
        );

        const lessonsByParentIdMap = new Map<string | null, typeof lessons>();

        for (const lesson of lessons) {
          const key = lesson.parentId ?? null;

          const lessonsByParent = lessonsByParentIdMap.get(key) ?? [];
          lessonsByParent.push(lesson);
          lessonsByParentIdMap.set(key, lessonsByParent);
        }

        cache.batch({
          update(cache) {
            for (const lesson of lessons) {
              const id = cache.identify(lesson);
              if (id) {
                cache.writeFragment({
                  id,
                  fragment: LessonFieldsFragmentDoc,
                  data: lesson,
                });
              }

              // Update lesson isTrash => false
              cache.modify({
                id: `Lesson:${lesson.id}`,
                fields: {
                  isTrash() {
                    return false;
                  },
                },
              });
            }

            for (const [parentId, lessons] of lessonsByParentIdMap.entries()) {
              const newRefs = lessons
                .map((p) => cache.identify(p))
                .filter(Boolean)
                .map((id) => ({ __ref: id }));

              // Add lessons to active page
              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  lessonsByParentId(existingData, { storeFieldName }) {
                    const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                    const isTrashKey = '"isTrash":false';

                    if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                      return existingData;
                    if (!existingData?.items) return existingData;

                    return {
                      ...existingData,
                      items: [...newRefs, ...existingData.items],
                      total: (existingData.total || 0) + newRefs.length,
                    };
                  },
                },
              });

              // Substracts parent itemCount
              if (parentId !== null) {
                cache.modify({
                  id: `Project:${parentId}`,
                  fields: {
                    itemCount(existingData = 0) {
                      return existingData + newRefs.length;
                    },

                    trashCount(existingData = 0) {
                      return existingData - newRefs.length;
                    },
                  },
                });
              }

              // Remove lesson from trash page
              cache.modify({
                id: "ROOT_QUERY",
                fields: {
                  lessonsByParentId(existingData, { storeFieldName, readField }) {
                    const key = parentId === null ? `"parentId":null` : `"parentId":"${parentId}"`;
                    const isTrashKey = '"isTrash":true';

                    if (!(storeFieldName.includes(key) && storeFieldName.includes(isTrashKey)))
                      return existingData;
                    if (!existingData?.items) return existingData;

                    return {
                      ...existingData,
                      items: existingData.items.filter(
                        (ref: Reference) => !lessons.some((p) => readField("id", ref) === p.id)
                      ),
                      total: (existingData.total || 0) - newRefs.length,
                    };
                  },
                },
              });

              // Decrement Trash count number
              if (parentId === null) {
                cache.modify({
                  id: "ROOT_QUERY",
                  fields: {
                    rootTrashCount(existingData) {
                      return existingData - newRefs.length;
                    },
                  },
                });
              }
            }
          },
        });
      } catch (error) {
        console.error("Restore lessons cache update failed:", error);
      }
    },
  });

  const restoreLessons = async (ids: string[]) => {
    return withToast(
      async () => {
        const result = await restoreLessonsMutation({ variables: { ids: ids } });

        const maskedLessons = result.data?.restoreLessons ?? [];
        const restoredLessons = maskedLessons.map((lesson) =>
          useFragment(LessonFieldsFragmentDoc, lesson)
        );

        return restoredLessons;
      },
      {
        loading: `Restoring ${ids.length} lesson${ids.length !== 1 ? "s" : ""}...`,
        success: `Successfully restored ${ids.length} lesson${ids.length !== 1 ? "s" : ""}`,
        error: "Failed to restore lessons. Please try again.",
      },
      "restoreLessons"
    );
  };

  return {
    restoreLessons,
    loading,
    error,
  };
};
